import { Grid } from '@mui/material';
import { Control, FieldValues, Path } from 'react-hook-form';
import StateField from './state-field';
import TextInput from './text-input';

// Define a type for field requirements
export type FieldRequirements = {
  readonly address1Required?: boolean;
  readonly address2Required?: boolean;
  readonly cityRequired?: boolean;
  readonly stateRequired?: boolean;
  readonly zipRequired?: boolean;
};

interface AddressFieldsProps<TFieldValues extends FieldValues> {
  readonly control: Control<TFieldValues>;
  readonly address1Name: Path<TFieldValues>;
  readonly address2Name: Path<TFieldValues>;
  readonly cityName: Path<TFieldValues>;
  readonly stateName: Path<TFieldValues>;
  readonly zipName: Path<TFieldValues>;
  readonly isReadOnly?: boolean;
  readonly allFieldsRequired?: boolean;
  readonly fieldRequirements?: FieldRequirements;
}

/**
 * A reusable component for address fields (address1, address2, city, state, zip)
 * Supports both editable and read-only modes
 * Can specify individual field requirements or use allFieldsRequired
 */
const AddressFields = <TFieldValues extends FieldValues>({
  control,
  address1Name,
  address2Name,
  cityName,
  stateName,
  zipName,
  isReadOnly = false,
  allFieldsRequired = true,
  fieldRequirements,
}: AddressFieldsProps<TFieldValues>) => {
  // Determine if each field is required
  const isAddress1Required = fieldRequirements
    ? (fieldRequirements.address1Required ?? allFieldsRequired)
    : allFieldsRequired;

  const isAddress2Required = fieldRequirements
    ? (fieldRequirements.address2Required ?? false)
    : false;

  const isCityRequired = fieldRequirements
    ? (fieldRequirements.cityRequired ?? allFieldsRequired)
    : allFieldsRequired;

  const isStateRequired = fieldRequirements
    ? (fieldRequirements.stateRequired ?? allFieldsRequired)
    : allFieldsRequired;

  const isZipRequired = fieldRequirements
    ? (fieldRequirements.zipRequired ?? allFieldsRequired)
    : allFieldsRequired;

  return (
    <>
      <Grid item xs={12}>
        <TextInput
          name={address1Name}
          control={control}
          label="Address Line 1"
          isReadOnly={isReadOnly}
          required={isAddress1Required}
        />
      </Grid>

      <Grid item xs={12}>
        <TextInput
          name={address2Name}
          control={control}
          label="Address Line 2"
          isReadOnly={isReadOnly}
          required={isAddress2Required}
        />
      </Grid>

      <Grid item xs={12} sm={5}>
        <TextInput
          name={cityName}
          control={control}
          label="City"
          isReadOnly={isReadOnly}
          required={isCityRequired}
        />
      </Grid>

      <Grid item xs={6} sm={3}>
        <StateField
          name={stateName}
          control={control}
          label="State"
          required={isStateRequired}
          isReadOnly={isReadOnly}
        />
      </Grid>

      <Grid item xs={6} sm={4}>
        <TextInput
          name={zipName}
          control={control}
          label="Zip Code"
          isReadOnly={isReadOnly}
          required={isZipRequired}
        />
      </Grid>
    </>
  );
};

export default AddressFields;
